import { useMemo } from 'react'
import {
  Avatar,
  Stack,
  styled,
  Typography as MuiTypography,
} from '@mui/material'
import { IMessage } from '@weblite/ts-action/message'
import { toLocale } from '$func/text'
import { dateDayFormatter } from '$func/time'
import { BadgeWrapper } from '$common/badge/styled-badge'
import { OneLineLinkRenderer } from '$comp/_app/components/leftPanel/components/shared-media/components/link-previewer/one-line-link-renderer'

const CenteredStack = styled(Stack)`
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
`
const Typography = styled(MuiTypography)`
  line-height: 20px;
`

export function LinkPreviewer({
  message,
  selected,
}: {
  message: IMessage
  selected: boolean
}) {
  const { links, date } = message
  const day = useMemo(() => dateDayFormatter.format(new Date(date)), [date])

  const { title, description, imageUrl } = useMemo(
    () =>
      links?.find(item => item.title || item.description || item.imageUrl) || {
        title: '',
        description: '',
        imageUrl: '',
      },
    [links],
  )
  if (!links || !links.length) return null
  const firstLinkUrl = links[0].url
  const linkTitle =
    title ||
    new URL(
      firstLinkUrl.includes('https://')
        ? firstLinkUrl
        : `https://${firstLinkUrl}`,
    ).hostname
      .replace('https://', '')
      .replace('www.', '')
  return (
    <Stack
      direction="row"
      sx={{ width: '100%', height: 'fit-content', padding: '8px 0 8px 12px' }}
      gap="8px"
      marginBottom="auto"
    >
      <CenteredStack sx={{ width: '45px' }}>
        <Typography color="gray" variant="body2">
          {toLocale(day)}
        </Typography>
      </CenteredStack>
      <Stack gap="4px" overflow="hidden" sx={{ marginLeft: 'auto' }}>
        <Typography variant="subtitle3" lineHeight="20px">
          {linkTitle.charAt(0).toUpperCase() + linkTitle.slice(1)}
        </Typography>
        {description && (
          <Typography variant="caption1" color="gray" noWrap>
            {description}
          </Typography>
        )}
        {links.map(link => (
          <OneLineLinkRenderer key={link.url} link={link.url} />
        ))}
      </Stack>

      <BadgeWrapper
        selected={selected}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Avatar
          sx={{
            width: '48px',
            height: '48px',
            borderRadius: '4px',
            backgroundColor: 'veryVeryLightGray',
          }}
          src={imageUrl}
        >
          <MuiTypography variant="body1" color="semiLightGray">
            {linkTitle[0].toUpperCase()}
          </MuiTypography>
        </Avatar>
      </BadgeWrapper>
    </Stack>
  )
}
